import React, { useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ChevronDown, Building, Plus, Check } from 'lucide-react';

const WorkspaceSelector = ({ collapsed = false }) => {
  const { selectedWorkspace, workspaces, loading, selectWorkspace } = useWorkspace();
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const handleWorkspaceChange = (workspace) => {
    selectWorkspace(workspace);
    setIsOpen(false);
    // Navigate to home when workspace changes
    navigate('/');
  };

  const handleCreateNew = async () => {
    if (!currentUser) return;
    
    setCreating(true);
    setIsOpen(false);
    
    try {
      // Create a new company document with initial data (same as CompaniesHome)
      const newCompany = {
        userId: currentUser.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        // Company Info
        name: '',
        tagline: '',
        logo: '',
        website: '',
        founded: '',
        location: '',
        stage: 'seed',
        
        // Metrics
        metrics: {
          seeking: '',
          growth: '',
          users: '',
          arr: ''
        },
        
        // Team
        teamMembers: [
          { name: '', role: '', bio: '', initials: '', email: '' }
        ],
        
        // Pitch Deck
        pitchDeck: {
          uploaded: false,
          fileName: '',
          slides: 0,
          lastUpdated: null
        },
        
        // Financials
        financials: {
          year1: '',
          year2: '',
          year3: '',
          year4: '',
          year5: '',
          burnRate: '',
          cac: '',
          ltv: '',
          churn: '',
          breakeven: ''
        },
        
        // Due Diligence
        documents: [
          { name: 'Articles of Incorporation', uploaded: false, required: true },
          { name: 'Cap Table & Equity Structure', uploaded: false, required: true },
          { name: 'IP Portfolio & Patents', uploaded: false, required: true },
          { name: 'Legal Compliance Report', uploaded: false, required: false },
          { name: 'Insurance Documentation', uploaded: false, required: false },
          { name: 'Previous Funding History', uploaded: false, required: false },
          { name: 'Board Meeting Minutes', uploaded: false, required: false },
          { name: 'Customer References', uploaded: false, required: false }
        ],
        
        // Product Demo
        productDemo: {
          videoUrl: '',
          videoName: '',
          lastUpdated: null,
          demoLink: '',
          features: [
            'AI-powered analytics dashboard',
            'Seamless integrations with 50+ tools',
            'Real-time reporting and insights',
            'Enterprise-grade security',
            'Mobile-responsive interface'
          ]
        }
      };

      const docRef = await addDoc(collection(db, 'companies'), newCompany);
      
      // Set the new company as the selected workspace
      const newWorkspace = {
        id: docRef.id,
        ...newCompany
      };
      
      selectWorkspace(newWorkspace);
      
      // Navigate to pitch builder for the new company
      navigate(`/pitch-builder/${docRef.id}`);
      
    } catch (error) {
      console.error('Error creating new company:', error);
      alert('Failed to create new workspace. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center rounded-lg bg-gray-100 ${collapsed ? 'justify-center p-2' : 'gap-3 px-4 py-3'}`}>
        <div className="w-8 h-8 bg-gray-300 rounded-lg animate-pulse flex-shrink-0" />
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <div className="h-4 bg-gray-300 rounded animate-pulse mb-1" />
            <div className="h-3 bg-gray-300 rounded w-2/3" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Selected Workspace Display */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        title={collapsed ? `${selectedWorkspace?.name || 'Workspace'}${selectedWorkspace?.tagline ? ` – ${selectedWorkspace.tagline}` : ''}` : undefined}
        className={`w-full flex items-center bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 ${
          collapsed ? 'justify-center p-2' : 'gap-3 px-4 py-3 text-left'
        }`}
      >
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-blue-200 to-purple-200 flex items-center justify-center text-sm font-bold text-blue-700">
          {selectedWorkspace?.logoUrl ? (
            selectedWorkspace.logoUrl.startsWith('http') ? (
              <img src={selectedWorkspace.logoUrl} alt={selectedWorkspace.name} className="w-8 h-8 object-cover rounded-lg" />
            ) : (
              <span>{selectedWorkspace.logo}</span>
            )
          ) : (
            <span>{selectedWorkspace?.name?.[0] || '?'}</span>
          )}
        </div>
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0">
              <div className="text-gray-900 font-medium truncate flex items-center gap-2">
                {selectedWorkspace?.name}
                {selectedWorkspace?.isDemo && (
                  <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                    Demo
                  </span>
                )}
              </div>
              <div className="text-gray-600 text-xs truncate">
                {selectedWorkspace?.tagline}
              </div>
            </div>
            <ChevronDown 
              size={16} 
              className={`text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} 
            />
          </>
        )}
        {collapsed && (
          <ChevronDown 
            size={14} 
            className={`text-gray-500 transition-transform absolute -right-0.5 top-1/2 -translate-y-1/2 ${isOpen ? 'rotate-180' : ''}`} 
          />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown - when collapsed, position to the right of sidebar with min-width */}
          <div className={`absolute top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-20 max-h-80 overflow-y-auto ${
            collapsed ? 'left-full ml-1 w-64' : 'left-0 right-0'
          }`}>
            {/* Create New Workspace */}
            <button
              onClick={handleCreateNew}
              disabled={creating}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center">
                {creating ? (
                  <div className="w-4 h-4 border-2 border-purple-200 border-t-purple-700 rounded-full animate-spin"></div>
                ) : (
                  <Plus size={16} className="text-purple-700" />
                )}
              </div>
              <div>
                <div className="font-semibold text-gray-900">
                  {creating ? 'Creating...' : 'Create New Workspace'}
                </div>
                <div className="text-sm text-gray-600">
                  {creating ? 'Setting up your new company' : 'Add a new company'}
                </div>
              </div>
            </button>

            {/* Workspaces List */}
            {workspaces.map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => handleWorkspaceChange(workspace)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 relative transition-colors"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-blue-200 to-purple-200 flex items-center justify-center text-sm font-bold text-blue-700">
                  {workspace.logoUrl ? (
                    workspace.logoUrl.startsWith('http') ? (
                      <img src={workspace.logoUrl} alt={workspace.name} className="w-8 h-8 object-cover rounded-lg" />
                    ) : (
                      <span>{workspace.logo}</span>
                    )
                  ) : (
                    <span>{workspace.name?.[0] || '?'}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate flex items-center gap-2">
                    {workspace.name}
                    {workspace.isDemo && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        Demo
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 truncate">
                    {workspace.tagline}
                  </div>
                </div>
                {selectedWorkspace?.id === workspace.id && (
                  <Check size={16} className="text-blue-600 flex-shrink-0" />
                )}
              </button>
            ))}

            {/* Empty State */}
            {workspaces.length === 0 && (
              <div className="px-4 py-8 text-center">
                <Building size={32} className="mx-auto text-gray-400 mb-2" />
                <div className="text-gray-700 text-sm font-medium">No workspaces yet</div>
                <div className="text-gray-500 text-xs mt-1">Create your first company workspace</div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WorkspaceSelector;
